import React, { useMemo } from 'react';

export interface TagWithCount {
    tag: string;
    count: number;
}

interface KeywordTagCloudProps {
    tagsWithCounts: TagWithCount[];
    onSelectKeyword: (keyword: string) => void;
    /** Maximum number of keywords to show (highest-used first). Default 40. */
    maxTags?: number;
    loading?: boolean;
}

/** Font weight 400–700 by count (min → 400, max → 700). */
function fontWeightForCount(count: number, min: number, max: number): number {
    if (max <= min) return 500;
    const ratio = (count - min) / (max - min);
    return Math.round(400 + ratio * 300);
}

/** Font size in rem: most-used bigger (maxCount → 1.25rem), least-used smaller (minCount → 0.75rem). */
function fontSizeForCount(count: number, min: number, max: number): number {
    if (max <= min) return 1;
    const ratio = (count - min) / (max - min);
    return 0.75 + ratio * 0.5;
}

function KeywordTagCloud({
    tagsWithCounts,
    onSelectKeyword,
    maxTags = 40,
    loading = false
}: KeywordTagCloudProps) {
    const { visibleTags, minCount, maxCount } = useMemo(() => {
        const limited = tagsWithCounts.slice(0, maxTags);
        if (limited.length === 0) return { visibleTags: [], minCount: 0, maxCount: 0 };
        const counts = limited.map((t) => t.count);
        return {
            visibleTags: limited,
            minCount: Math.min(...counts),
            maxCount: Math.max(...counts)
        };
    }, [tagsWithCounts, maxTags]);

    if (loading) {
        return (
            <div className="keyword-tag-cloud card border-0 shadow-sm mt-3">
                <div className="card-body p-4">
                    <h5 className="card-title mb-3">Keywords</h5>
                    <p className="small text-muted mb-0">Loading…</p>
                </div>
            </div>
        );
    }

    if (visibleTags.length === 0) {
        return (
            <div className="keyword-tag-cloud card border-0 shadow-sm mt-3">
                <div className="card-body p-4">
                    <h5 className="card-title mb-3">Keywords</h5>
                    <p className="small text-muted mb-0">No keywords available.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="keyword-tag-cloud card border-0 shadow-sm mt-3">
            <div className="card-body p-4">
                <h5 className="card-title mb-3">Keywords</h5>
                <div className="keyword-tag-cloud-list">
                    {visibleTags.map(({ tag, count }) => (
                        <button
                            key={tag}
                            type="button"
                            className="keyword-tag-cloud-tag btn btn-sm"
                            style={{
                                fontWeight: fontWeightForCount(count, minCount, maxCount),
                                fontSize: `${fontSizeForCount(count, minCount, maxCount)}rem`
                            }}
                            onClick={() => onSelectKeyword(tag)}
                            title={`${count} video${count !== 1 ? 's' : ''}`}
                        >
                            <span className="keyword-tag-cloud-tag-label">{tag}</span>
                            <span className="keyword-tag-cloud-tag-count">({count})</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default KeywordTagCloud;
